/**
 * Patient Copay Program — field-level effects.
 *
 * Migrated from: patient/patient-copay-program-base.coffee
 *
 * Old validators → new effects:
 *   StatusChange  → CopayStatusChange
 *   CheckForAlert → CopayCheckForAlert
 *   SetCapAmount  → SetCapAmount
 *
 * Biosimilar deny fork (same pattern as PAP Denied → Biosimilar).
 */
import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

import { syncLocalBiosimilarPreview } from "../shared/sync-local-biosimilar-preview";

import type { Form } from "@formily/core";

const BIOSIMILAR_DENY_FIELDS = [
	"is_biosimilar_available",
	"biosimilar_brand_offered",
	"biosimilar_drug_id",
] as const;

const DENY_340B_FIELDS = [
	"is_340b_available",
	"start_340b_app",
	"is_provider_available",
] as const;

function clearCopayDenyAlternatives(form: Form) {
	for (const name of [...BIOSIMILAR_DENY_FIELDS, ...DENY_340B_FIELDS]) {
		form.setFieldState(name, (s) => {
			s.value = undefined;
		});
	}
}

/** Keeps case `subform_biosimilar` in sync for UI before case Save (local only). */
export const CopaySyncLocalBiosimilarPreview: FieldHandler = (field, props) => {
	syncLocalBiosimilarPreview({
		form: props.form,
		parentForm: props.parentForm,
		createFrom: "Patient Copay Form",
	});
	return "";
};

// ─── CopayStatusChange ──────────────────────────────────────────────────────
// When Denied: look up 340B availability from Short Brand (same pattern as PAP).
// When leaving Denied: clear biosimilar + 340B deny-alt fields.
let copayStatusChangeSeq = 0;

export const CopayStatusChange: FieldHandler = (field, props) => {
	const { form, parentForm } = props;
	const status = form.getFieldState("status")?.value ?? field.value;

	if (status !== "Denied") {
		copayStatusChangeSeq += 1;
		clearCopayDenyAlternatives(form);
		CopaySyncLocalBiosimilarPreview(field, props);
		return "";
	}

	const seq = ++copayStatusChangeSeq;

	void (async () => {
		const medId = parentForm?.getFieldState("medication_id")?.value?.id;
		if (!medId) return;

		try {
			const invData = await fetchAPI(buildApiUrl(`/inventory/${medId}`));
			if (seq !== copayStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv.brand_name_id?.id) return;

			const brandData = await fetchAPI(
				buildApiUrl(`/drug_brand_short/${inv.brand_name_id?.id}`)
			);
			if (seq !== copayStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const brand = Array.isArray(brandData) ? brandData[0] : brandData;
			if (!brand) return;

			const is340bFound = brand.discount_drug_program_available || "No";
			form.setFieldState("is_340b_available", (s) => {
				s.value = is340bFound;
			});
			CopaySyncLocalBiosimilarPreview(field, props);
		} catch (err) {
			console.error("CopayStatusChange: API error", err);
		}
	})();
	CopaySyncLocalBiosimilarPreview(field, props);
	return "";
};

// Clears brand/drug when biosimilar availability leaves Yes.
export const CopayBiosimilarAvailabilityChange: FieldHandler = (
	field,
	props
) => {
	const { form } = props;
	const available =
		form.getFieldState("is_biosimilar_available")?.value ?? field.value;
	if (available !== "Yes") {
		form.setFieldState("biosimilar_brand_offered", (s) => {
			s.value = undefined;
		});
		form.setFieldState("biosimilar_drug_id", (s) => {
			s.value = undefined;
		});
	}
	CopaySyncLocalBiosimilarPreview(field, props);
	return "";
};

// Clears Biosimilar Drugs when Brands Offered changes (user edit only).
export const CopayBiosimilarBrandChange: FieldHandler = (field, props) => {
	const { form } = props;
	const modified = Boolean(
		(field as { modified?: boolean; selfModified?: boolean }).modified ||
		(field as { modified?: boolean; selfModified?: boolean }).selfModified
	);
	if (modified) {
		form.setFieldState("biosimilar_drug_id", (s) => {
			s.value = undefined;
		});
	}
	CopaySyncLocalBiosimilarPreview(field, props);
	return "";
};

// ─── CopayCheckForAlert ─────────────────────────────────────────────────────
export const CopayCheckForAlert: FieldHandler = (field, { form }) => {
	form.setFieldState("show_alert", (s) => {
		s.value = "No";
	});

	const status = form.getFieldState("status")?.value;
	if (status !== "Denied") return "";

	const startBiosimilar = form.getFieldState(
		"is_biosimilar_available"
	)?.value;
	if (startBiosimilar === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "Biosimilar Program will be added to the case";
		});
	}

	const start340bApp = form.getFieldState("start_340b_app")?.value;
	if (start340bApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "340B/discount drug program added to the case";
		});
	}

	return "";
};

// ─── SetCapAmount ───────────────────────────────────────────────────────────
export const SetCapAmount: FieldHandler = (field, { form }) => {
	void (async () => {
		const programId = form.getFieldState("program_id")?.value?.id;
		const capStatus = form.getFieldState("status")?.value;

		if (!programId || capStatus !== "Approved") return;

		try {
			const data = await fetchAPI(
				buildApiUrl(`/copay_program/${programId}`)
			);
			const program = Array.isArray(data) ? data[0] : data;
			if (!program) return;

			const approvedAmount = form.getFieldState("approved_amount")?.value;
			const amountLeft = form.getFieldState("amount_left")?.value;

			if (!approvedAmount) {
				form.setFieldState("approved_amount", (s) => {
					s.value = program.maximum_amount;
				});
			}
			if (!amountLeft) {
				form.setFieldState("amount_left", (s) => {
					s.value = program.maximum_amount;
				});
			}
		} catch (err) {
			console.error("SetCapAmount: API error", err);
		}
	})();
	return "";
};
